import React, { useCallback, useEffect, useState } from 'react';
import { LucideIcon } from 'lucide-react';

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  shortcut?: string;
  disabled?: boolean;
  divider?: boolean;
  onClick?: () => void;
}

interface CanvasContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

export const CanvasContextMenu: React.FC<CanvasContextMenuProps> = ({ x, y, items, onClose }) => {
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    // Adjust position if menu would go off screen
    const menu = document.getElementById('canvas-context-menu');
    if (menu) {
      const rect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (x + rect.width > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      if (y + rect.height > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      setPosition({ x: adjustedX, y: adjustedY });
    }
  }, [x, y]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const menu = document.getElementById('canvas-context-menu');
      if (menu && !menu.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleItemClick = useCallback((item: ContextMenuItem) => {
    if (!item.disabled && item.onClick) {
      item.onClick();
      onClose();
    }
  }, [onClose]);

  return (
    <div
      id="canvas-context-menu"
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
        minWidth: '220px',
        backgroundColor: '#2B2B2B',
        color: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
        padding: '6px',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '13px',
      }}
    >
      {items.map((item, index) => {
        if (item.divider) {
          return (
            <div
              key={`divider-${index}`}
              style={{
                height: '1px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                margin: '4px 0',
              }}
            />
          );
        }

        const Icon = item.icon;
        const isHeader = item.disabled && !item.onClick && !Icon;
        
        return (
          <button
            key={item.id}
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            style={{
              width: '100%',
              padding: isHeader ? '8px 8px 4px 8px' : '6px 8px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: isHeader 
                ? 'rgba(255, 255, 255, 0.6)' 
                : item.disabled 
                  ? 'rgba(255, 255, 255, 0.4)' 
                  : '#FFFFFF',
              textAlign: 'left',
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'background-color 0.1s',
              fontSize: isHeader ? '11px' : '13px',
              fontWeight: isHeader ? '600' : 'normal',
              textTransform: isHeader ? 'uppercase' : 'none',
              letterSpacing: isHeader ? '0.5px' : 'normal',
            }}
            onMouseEnter={(e) => {
              if (!item.disabled && !isHeader) {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {Icon && <Icon size={16} strokeWidth={1.5} />}
              <span>{item.label}</span>
            </span>
            {item.shortcut && (
              <span
                style={{
                  fontSize: '11px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontFamily: 'monospace',
                  marginLeft: '24px',
                }}
              >
                {item.shortcut}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
